import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { books } from "@/data/books";
import { eduMaterials } from "@/data/edu";

const CartContext = createContext(null);

const STORAGE_KEY = "tk-cart-v1";

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

const catalog = [...books, ...eduMaterials];
const catalogMap = catalog.reduce((acc, item) => {
  acc[item.lookup_key] = item;
  return acc;
}, {});

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => readStorage());
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((lookup_key, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.lookup_key === lookup_key);
      if (existing) {
        return prev.map((i) =>
          i.lookup_key === lookup_key ? { ...i, quantity: i.quantity + qty } : i,
        );
      }
      return [...prev, { lookup_key, quantity: qty }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((lookup_key) => {
    setItems((prev) => prev.filter((i) => i.lookup_key !== lookup_key));
  }, []);

  const setQuantity = useCallback((lookup_key, qty) => {
    setItems((prev) =>
      prev.map((i) => (i.lookup_key === lookup_key ? { ...i, quantity: Math.max(1, qty) } : i)),
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const detailed = useMemo(
    () =>
      items
        .map((i) => ({ ...i, product: catalogMap[i.lookup_key] }))
        .filter((i) => i.product),
    [items],
  );

  const total = useMemo(
    () => detailed.reduce((sum, i) => sum + i.product.price_pln * i.quantity, 0),
    [detailed],
  );

  const count = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      detailed,
      total,
      count,
      isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      toggleCart: () => setIsOpen((v) => !v),
      addItem,
      removeItem,
      setQuantity,
      clear,
    }),
    [items, detailed, total, count, isOpen, addItem, removeItem, setQuantity, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
